import {Trans} from '@lingui/macro';
import {Duration} from 'luxon';
import parseDuration from 'parse-duration';
import React, {FC, ReactNode, useMemo, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {SimpleDropdown} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {formatDuration} from '../../helpers/dateTimeHelpers';
import {SettingsFieldLine} from '../common/settingsFieldLine';

/*
 * Props.
 */

interface SettingsInboxesInactivityTimeoutFieldProps {
  inactivityTimeout: number;
  onTimeoutSelect: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledParsedDurationLabel = styled.div`
  color: ${(p) => p.theme.greys.shade50};
`;

/*
 * Component.
 */

export const SettingsInboxesInactivityTimeoutField: FC<SettingsInboxesInactivityTimeoutFieldProps> = (
  props,
) => {
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [promptValue, setPromptValue] = useState('24h');
  const parsedPromptValue = useMemo(() => parseDuration(promptValue) || 0, [promptValue]);
  const {onTimeoutSelect, inactivityTimeout} = props;

  const renderValue = (): ReactNode => {
    if (inactivityTimeout <= 0) {
      return <Trans id="settingsinboxes-inactivitytimeout-never">Never</Trans>;
    }

    const durationObject = Duration.fromMillis(inactivityTimeout);

    return formatDuration(durationObject, {unitDisplay: 'short'});
  };

  const maybeRenderPrompt = () => {
    if (!isPromptOpen) {
      return null;
    }

    return (
      <OkCancelDialog
        title={<Trans id="settingsinboxes-inactivitytimeout-customvalue">Custom inactivity value</Trans>}
        onRequestClose={() => setIsPromptOpen(false)}
        onOkClick={() => {
          onTimeoutSelect(parsedPromptValue);
          setIsPromptOpen(false);
        }}
        width={460}
        isOkDisabled={parsedPromptValue <= 0}
      >
        <Trans id="settingsinboxes-inactivitytimeout-copy">
          Enter the inactivity duration for this inbox (e.g: 2h, 5d, 15m)
        </Trans>
        <TextBox size={VisualSizesEnum.LARGE} value={promptValue} onChange={setPromptValue} shouldAutoFocus />
        <StyledParsedDurationLabel>
          {formatDuration(Duration.fromMillis(parsedPromptValue), {showZero: parsedPromptValue <= 0})}
        </StyledParsedDurationLabel>
      </OkCancelDialog>
    );
  };

  return (
    <>
      {maybeRenderPrompt()}
      <SettingsFieldLine
        title={
          <Trans id="settingsinboxes-inactivitytimeout-fieldtitle">Conversations become inactive after</Trans>
        }
        subtitle={
          <Trans id="settingsinboxes-inactivitytimeout-fieldcopy">
            If a conversation receives a new message after becoming inactive, it will be considered as new in
            the analytics.
          </Trans>
        }
        content={
          <DropdownBoxCoordinator
            label={undefined}
            value={renderValue()}
            renderDropdown={(popoverProps) => (
              <SimpleDropdown {...popoverProps}>
                {[
                  {
                    title: <Trans id="settingsinboxes-inactivitytimeout-4h">4 hours</Trans>,
                    value: Duration.fromObject({hours: 4}).toMillis(),
                  },
                  {
                    title: <Trans id="settingsinboxes-inactivitytimeout-1d">1 day</Trans>,
                    value: Duration.fromObject({day: 1}).toMillis(),
                  },
                  {
                    title: <Trans id="settingsinboxes-inactivitytimeout-5d">5 days</Trans>,
                    value: Duration.fromObject({day: 5}).toMillis(),
                  },
                  {
                    title: <Trans id="settingsinboxes-inactivitytimeout-never">Never</Trans>,
                    value: -1,
                  },
                ].map((item) => (
                  <SimpleDropdownItem
                    isChecked={inactivityTimeout === item.value}
                    key={item.value}
                    onSelect={() => onTimeoutSelect(item.value)}
                  >
                    {item.title}
                  </SimpleDropdownItem>
                ))}
                <SimpleDropdownItem onSelect={() => setIsPromptOpen(true)}>
                  <Trans id="settingsinboxes-inactivitytimeout-custom">Custom</Trans>
                </SimpleDropdownItem>
              </SimpleDropdown>
            )}
          />
        }
        isSubtitleMultiline
      />
    </>
  );
};
